import { useState, useEffect, useRef, useMemo } from "react";
import { FaTrash, FaEdit, FaPlus } from "react-icons/fa"; // Iconos
import { Note, Sidebar, formats } from "../components/Note";
import { ACCESS_TOKEN } from "../constants";
import UserMenu from "../components/UserMenu";
import api from "../api";
import ThemeToggleButton from "../components/ThemeToggleButton";

// import "../styles/NoteDashboard.css";

function Plantillas() {
  const [notes, setNotes] = useState([]);
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [dark, setDark] = useState(localStorage.getItem("theme") === "dark");
  const [showModal, setShowModal] = useState(false);
  const quillRef = useRef(null);
  const [editor, setEditor] = useState({ Comp: null, error: null });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  // Persistir tema oscuro
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  // Cargar React Quill dinámicamente
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const mod = await import("react-quill");
        await import("react-quill/dist/quill.snow.css");
        await import("quill-emoji/dist/quill-emoji.css");
        await import("quill-emoji");
        if (mounted) setEditor({ Comp: mod.default, error: null });
      } catch (e) {
        if (mounted) setEditor({ Comp: null, error: e });
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => getNotes(), []);

  const getNotes = () => {
    api.get("notes/")
      .then(res => setNotes(res.data))
      .catch(err => alert(err));
  };

  const deleteNote = (id) => {
    api.delete(`notes/delete/${id}/`)
      .then(res => {
        if (res.status === 204) alert("Plantilla eliminada");
        getNotes();
        if (editingId === id) resetForm();
      })
      .catch(err => alert(err));
  };

  const startEdit = (note) => {
    setEditingId(note.id);
    setTitle(note.title);
    setContent(note.content);
    window.scrollTo({ top: 0, behavior: "smooth" });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingId(null);
    setTitle("");
    setContent("");
    setShowModal(false);
  };

  const submitNote = (e) => {
    e.preventDefault();
    const plain = content.replace(/<(.|\n)*?>/g, "").trim();
    if (!title.trim() || !plain) {
      alert("Título y contenido son requeridos");
      return;
    }
    // Bloquear rutas locales en imágenes (file://, C:\\, \\\\host)
    try {
      const localPathRe = /<img[^>]+src=\"([^\"]+)\"/ig;
      let m, hasLocal = false;
      while ((m = localPathRe.exec(content)) !== null) {
        const src = String(m[1] || '').trim();
        if (/^file:/i.test(src) || /^[a-zA-Z]:[\\/]/.test(src) || /^\\\\/.test(src)) {
          hasLocal = true; break;
        }
      }
      if (hasLocal) {
        alert("Detectamos imágenes con rutas locales (p. ej., C:, file://). Usa el botón de imagen del editor para subirla y que funcione en los envíos.");
        return;
      }
    } catch(_) {}
    const payload = { content, title };
    const request = editingId
      ? api.put(`notes/${editingId}/`, payload)
      : api.post("notes/", payload);
    request
      .then(() => {
        alert(editingId ? "Plantilla actualizada" : "Plantilla guardada");
        resetForm();
        getNotes();
      })
      .catch(err => alert("Error: " + (err?.response?.status || "")));
  };

  const toggleExpand = (id) => {
    setNotes(prev => prev.map(n => n.id === id ? { ...n, expanded: !n.expanded } : n));
  };

  const EditorComp = editor.Comp;
  const editorModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ font: [] }, { size: [] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ header: '1' }, { header: '2' }, 'blockquote', 'code-block'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['image', 'emoji', 'video'],
        ['clean']
      ],
      handlers: {
        image: function () {
          try {
            const input = document.createElement('input');
            input.setAttribute('type', 'file');
            input.setAttribute('accept', 'image/*');
            input.onchange = async () => {
              const file = input.files && input.files[0];
              if (!file) return;
              try {
                setUploadingImage(true);
                const fd = new FormData();
                fd.append('image', file);
                const r = await api.post('uploads/images/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                const url = r?.data?.image || r?.data?.url;
                const q = quillRef.current && (quillRef.current.getEditor ? quillRef.current.getEditor() : quillRef.current.editor);
                if (q && q.insertEmbed && url) {
                  const range = q.getSelection(true);
                  const pos = range ? range.index : q.getLength();
                  q.insertEmbed(pos, 'image', url, 'user');
                  q.setSelection(pos + 1);
                }
              } catch (e) {
                alert('No se pudo subir la imagen');
              } finally {
                setUploadingImage(false);
              }
            };
            input.click();
          } catch (e) {}
        },
        video: function () {
          try {
            const input = document.createElement('input');
            input.setAttribute('type', 'file');
            input.setAttribute('accept', 'video/*');
            input.onchange = async () => {
              const file = input.files && input.files[0];
              if (!file) return;
              try {
                setUploadingVideo(true);
                const fd = new FormData();
                fd.append('video', file);
                const r = await api.post('uploads/videos/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                const url = r?.data?.video || r?.data?.url;
                const q = quillRef.current && (quillRef.current.getEditor ? quillRef.current.getEditor() : quillRef.current.editor);
                if (q && url) {
                  const range = q.getSelection(true);
                  const pos = range ? range.index : q.getLength();
                  const text = 'Video adjunto';
                  // Insertar un enlace de texto en lugar de <video>
                  q.insertText(pos, text, { link: url }, 'user');
                  q.insertText(pos + text.length, ' ', 'user');
                  q.setSelection(pos + text.length + 1);
                } else if (url) {
                  // Fallback HTML si no hay instancia de Quill
                  const html = `<p><a href="${url}" target="_blank" rel="noopener">Video adjunto</a></p>`;
                  setContent((prev) => html + (prev || ''));
                }
              } catch (e) {
                alert('No se pudo subir el video');
              } finally {
                setUploadingVideo(false);
              }
            };
            input.click();
          } catch (e) {}
        }
      }
    },
    'emoji-toolbar': true,
    'emoji-shortname': true
  }), []);

  return (
    <div className="min-h-screen flex bg-gray-100 dark:bg-gray-900 transition-colors duration-500">
      <Sidebar />

      <main className="flex-1 p-8 bg-gray-50 dark:bg-gray-900 transition-colors duration-500">
        {/* === Header / Panel superior (rediseñado) === */}
        <div
          className={`rounded-2xl shadow-md p-6 mb-8 border transition-colors duration-500
            ${dark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"}`}
        >
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
            {/* Izquierda: Ícono + título + botón */}
            <div className="flex items-start gap-4">
              {/* Ícono decorativo con degradado */}
              <div
                className="flex items-center justify-center rounded-xl w-16 h-16 shadow-md"
                style={{
                  background: dark
                    ? "linear-gradient(135deg,#0ea5e9,#7c3aed)"
                    : "linear-gradient(135deg,#34d399,#06b6d4)",
                  color: "#fff",
                  fontSize: "1.5rem",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                }}
              >
                📝
              </div>

              {/* Título + botón */}
              <div className="flex flex-col gap-3">
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                  Plantillas
                </h1>

                <button
                  onClick={() => setShowModal(true)}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold shadow-md transition-all transform active:scale-95
                    ${dark
                      ? "bg-gradient-to-r from-emerald-500 to-cyan-500 text-gray-900 hover:from-emerald-400 hover:to-cyan-400"
                      : "bg-gradient-to-r from-emerald-500 to-cyan-600 text-white hover:from-emerald-600 hover:to-cyan-700"}
                  `}
                  style={{ border: "none", cursor: "pointer" }}
                >
                  <FaPlus className="inline mr-2" /> Nueva Plantilla
                </button>
              </div>
            </div>

            {/* Derecha: tema y usuario */}
            <div className="flex items-center gap-3">
              {/* Botón tema */}
              <button
                onClick={() => setDark(!dark)}
                className={`px-4 py-2 rounded-xl text-sm font-medium shadow-md transition-transform active:scale-95
                  ${dark
                    ? "bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 hover:from-yellow-500 hover:to-yellow-600"
                    : "bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:from-indigo-600 hover:to-purple-700"}
                `}
              >
                {dark ? "☀ Claro" : "🌙 Oscuro"}
              </button>

              {/* Menú usuario */}
              <UserMenu usernameProp={localStorage.getItem("username")} />
            </div>


            
          </div>
        </div>
        {/* Tabla de plantillas con expansión */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700">
            {/* Encabezado */}
            <div className="grid grid-cols-3 gap-0 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-700 dark:to-gray-800 px-6 py-4 font-semibold text-gray-700 dark:text-gray-200">
              <div>Título</div>
              <div>Contenido</div>
              <div className="text-right">Acción</div>
            </div>

  {/* Filas */}
  {notes.length > 0 ? (
    notes.map((note) => (
      <div key={note.id} className="border-b border-gray-200 dark:border-gray-700">
        {/* Fila principal */}
        <div
          onClick={() => toggleExpand(note.id)}
          className="grid grid-cols-3 gap-0 px-6 py-4 items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition"
        >
          <div className="font-medium text-gray-800 dark:text-gray-100">{note.title}</div>
          <div className="truncate">
            {/* Forzar contraste también en el snippet truncado */}
            <style>{`
              .tpl-snippet, .tpl-snippet * { color: #475569 !important; }
              .dark .tpl-snippet, .dark .tpl-snippet * { color: #cbd5e1 !important; }
            `}</style>
            <div
              className="tpl-snippet"
              dangerouslySetInnerHTML={{
                __html:
                  (note.content || '')
                    .replace(/<[^>]+>/g, '')
                    .slice(0, 100) + (note.content && note.content.length > 100 ? '…' : ''),
              }}
            />
          </div>

          <div
            className="flex justify-end gap-3"
            onClick={(e) => e.stopPropagation()} // evita que el clic en los botones expanda
          >
            <button
              onClick={() => startEdit(note)}
              className="text-yellow-400 hover:text-yellow-500 text-lg transition"
              title="Editar"
            >
              <FaEdit />
            </button>
            <button
              onClick={() => deleteNote(note.id)}
              className="text-red-500 hover:text-red-600 text-lg transition"
              title="Eliminar"
            >
              <FaTrash />
            </button>
          </div>
        </div>

        {/* Fila expandida */}
        <div
          className={`overflow-hidden transition-all duration-300 ${
            note.expanded ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="px-6 py-3 bg-gray-50 dark:bg-gray-900 text-sm">
            {/* Forzar contraste en preview expandido */}
            <style>{`
              .tpl-preview, .tpl-preview * { color: #111827 !important; }
              .dark .tpl-preview, .dark .tpl-preview * { color: #e5e7eb !important; }
              .tpl-preview a { text-decoration: underline; }
            `}</style>
            <div className="tpl-preview" dangerouslySetInnerHTML={{ __html: note.content }} />
          </div>
        </div>
      </div>
    ))
  ) : (
    <div className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
      No hay plantillas disponibles
    </div>
  )}
</div>

{/* ALTERNATIVA DE TABLA
        <section className="flex flex-col gap-4 mb-8">
          {notes.map(note => (
            <div key={note.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 flex flex-col">
              <div className="flex justify-between items-start cursor-pointer" onClick={() => toggleExpand(note.id)}>
                <div>
                  <div className="font-medium text-gray-800 dark:text-gray-100">{note.title}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {note.expanded ? (
                      <div dangerouslySetInnerHTML={{ __html: note.content }} />
                    ) : (
                      <div dangerouslySetInnerHTML={{ __html: note.content?.slice(0, 70) + (note.content && note.content.length > 70 ? "…" : "") }} />
                    )}
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-2">
                <button onClick={() => startEdit(note)} className="text-yellow-400 hover:text-yellow-500 transition text-xl" title="Editar">
                  <FaEdit />
                </button>
                <button onClick={() => deleteNote(note.id)} className="text-red-500 hover:text-red-600 transition text-xl" title="Eliminar">
                  <FaTrash />
                </button>
              </div>
            </div>
          ))}
        </section>*/}
        
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm transition-all">
            {/* 💡 ESTILOS DIRECTOS PARA QUILL */}
            <style>{`
              /* === Editor Quill en modo claro === */
              .ql-toolbar {
                background-color: #f9fafb !important;
                border-color: #d1d5db !important;
              }
              .ql-container {
                background-color: #ffffff !important;
                border-color: #d1d5db !important;
                color: #111827 !important;
              }
              .ql-editor {
                color: #111827 !important;
                background-color: #ffffff !important;
              }

              /* === Editor Quill en modo oscuro === */
              .dark .ql-toolbar {
                background-color: #1f2937 !important;
                border-color: #374151 !important;
                color: #e5e7eb !important;
              }
              .dark .ql-container {
                background-color: #111827 !important;
                border-color: #374151 !important;
                color: #e5e7eb !important;
              }
              .dark .ql-editor {
                color: #e5e7eb !important;
                background-color: #111827 !important;
              }
              .dark .ql-editor::before {
                color: #9ca3af !important;
                opacity: 0.8;
              }
            `}</style>

            <div
              className={`w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded-2xl border transition-colors duration-500 shadow-[0_0_30px_rgba(0,0,0,0.7)]
                ${dark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200 shadow-[0_0_25px_rgba(0,0,0,0.15)]"}
              `}
            >
              {/* Header */}
              <div
                className={`flex justify-between items-center px-6 py-4 border-b transition-colors duration-500
                  ${dark ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-gray-100"}
                `}
              >
                <h2
                  className={`text-xl font-semibold transition-colors duration-500
                    ${dark ? "text-gray-100" : "text-gray-800"}
                  `}
                >
                  {editingId ? "Editar Plantilla" : "Nueva Plantilla"}
                </h2>
                <button
                  onClick={resetForm}
                  className={`text-2xl font-bold transition hover:scale-110
                    ${dark ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700"}
                  `}
                >
                  &times;
                </button>
              </div>

              {/* Formulario */}
              <form onSubmit={submitNote} className="flex flex-col gap-4 px-6 py-5">
                {/* Título */}
                <div>
                  <input
                    type="text"
                    maxLength={20}
                    placeholder="Título (máx 20 caracteres)"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className={`w-full px-4 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition
                      ${dark
                        ? "bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-800 placeholder-gray-500"}
                    `}
                  />
                  <div className={`text-xs text-right mt-1 ${dark ? "text-gray-500" : "text-gray-500"}`}>
                    {title.length}/20
                  </div>
                </div>

                {/* Editor Quill */}
                {EditorComp ? (
                  <div
                    className={`rounded-lg border overflow-hidden transition-colors duration-500
                      ${dark ? "border-gray-700 bg-gray-800" : "border-gray-300 bg-white"}
                    `}
                  >
                    <EditorComp
                      theme="snow"
                      value={content}
                      onChange={setContent}
                      modules={editorModules}
                      formats={formats}
                      placeholder="Contenido de la plantilla..."
                      ref={quillRef}
                      style={{
                        minHeight: "220px",
                        color: dark ? "#e5e7eb" : "#111827",
                        backgroundColor: dark ? "#111827" : "#ffffff",
                      }}
                    />
                  </div>
                ) : (
                  <div
                    className={`p-4 border rounded-md text-sm italic text-center transition-colors duration-500
                      ${dark
                        ? "border-gray-700 bg-gray-800 text-gray-400"
                        : "border-gray-300 bg-gray-50 text-gray-600"}`}
                  >
                    Cargando editor...
                  </div>
                )}

                {/* Botones */}
                <div className="flex justify-end gap-3 mt-4">
                  <button
                    type="submit"
                    className={`px-6 py-2 rounded-lg font-medium shadow-md transition-transform active:scale-95
                      ${dark
                        ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-gray-900 hover:from-blue-400 hover:to-indigo-400"
                        : "bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700"}
                    `}
                  >
                    {editingId ? "Actualizar" : "Guardar Plantilla"}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className={`px-6 py-2 rounded-lg font-medium shadow-md transition-transform active:scale-95
                      ${dark
                        ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
                        : "bg-gray-300 text-gray-800 hover:bg-gray-400"}
                    `}
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}

export default Plantillas;
